/**
 * Bracket strategies (SPEC §9.4; owner 2026-09-27 "mimic the way bracket orders can be preset and
 * selected like in Tradovate"; research in DESIGN-REFERENCE "Tradovate bracket strategies").
 *
 * A preset is a named, reusable exit plan: a default quantity split into 1..MAX_LEGS legs, each
 * with its own profit target and stop loss, plus optional auto-trail and auto-breakeven. It is
 * stored once per user in the synced document `bracket_presets/main` (field merge, SPEC §1A.3).
 *
 * The stored form is **integer ticks only** (distances from the entry fill, always positive; the
 * side decides the direction when the bracket is placed). The editor may take points or $ and
 * converts here with exact integer/bigint math against the contract's tick size and tick value —
 * never a float in anything that is saved. Everything is pure so the rules are unit-tested.
 *
 * Nothing here sends orders. `brackets/order.ts` turns the selected preset into the bracket of an
 * order (quantity scaling, $ re-derived at submit — SPEC §9.4, Q-B1); the gateway manages it
 * (ADR-0025).
 *
 * Template units (ADR-0049, owner 2026-10-03 "base them off points or dollars per contract"): a
 * preset has a `unit` — ticks, points, or $ per contract. In points / $ every offset keeps the
 * value as typed (`…Entered`) beside its ticks; the ticks are re-derived from it for the contract
 * actually traded (`resolvePreset`) and the UI shows the ticks (and their exact points / $) before
 * anything is sent. Rounding (owner revision 2026-10-04): every field — stops, targets, triggers,
 * trail distance and step, breakeven offset — goes to the nearest tick, halves away from zero
 * (`roundingFor`). A leg may place a target only or a stop only (`null`), never neither.
 */
import { roundHalfAway } from "../trading/pnl.ts";

export const BRACKET_DOC_ID = "main";
export const BRACKET_DOC_VERSION = 2;
/** SPEC §9.4 MVP: up to 3 targets. */
export const MAX_LEGS = 3;
export const MAX_PRESETS = 50;
export const MAX_PRESET_NAME = 32;
export const MAX_PRESET_QTY = 99;
/** Sanity cap for any offset (NQ: 4,000 ticks = 1,000 points). */
export const MAX_OFFSET_TICKS = 4000;
/** `selected[root]` value for an explicit "No bracket" choice (SPEC §9.4: shown in amber). */
export const NO_BRACKET = "none";

export type BracketLeg = {
  /** Contracts this leg exits. */
  qty: number;
  /** Profit target distance from entry, ticks (> 0); null = this leg places no target (stop only). */
  targetTicks: number | null;
  /** Stop loss distance from entry, ticks (> 0); null = this leg places no stop (target only). */
  stopTicks: number | null;
  /**
   * Older documents only (unit "ticks"): the target / stop as entered in $ (integer cents, across
   * this leg's `qty`). SPEC §9.4 (Q-B1, resolved 2026-09-28 per the spec): the order re-derives the
   * ticks from it at submit for the leg's actual quantity; the ticks above are the preview at `qty`.
   * The editor no longer writes it (ADR-0049: $ is per contract now) but keeps honouring it.
   */
  targetCents?: number;
  stopCents?: number;
  /** The target / stop as typed in the preset's unit (points or $ per contract; ADR-0049). */
  targetEntered?: string;
  stopEntered?: string;
};

/**
 * Auto-trail (Tradovate ATM "Auto Trail": Profit Trigger / Stop Loss / Frequency; Rithmic 330
 * `trailing_stop_trigger_ticks`): once the trade is `triggerTicks` in profit the stop jumps to
 * `distanceTicks` behind price, then follows it in `stepTicks` increments. Tradovate's worked
 * example: long 1000, stop 990, trigger 10 → at 1010 the stop moves to 1005 (distance 5), then
 * 2 ticks per 2 ticks gained (step 2).
 */
export type TrailSettings = {
  triggerTicks: number;
  distanceTicks: number;
  stepTicks: number;
  /** As typed in the preset's unit (ADR-0049). */
  triggerEntered?: string;
  distanceEntered?: string;
  stepEntered?: string;
};

/**
 * Auto-breakeven (Tradovate ATM "Auto Breakeven": Profit Trigger / Offset; Rithmic 330
 * `break_even_trigger_ticks` / `break_even_ticks`): once the trade is `triggerTicks` in profit the
 * stop moves to entry + `offsetTicks` in the trade's favour. As in Tradovate the offset may be
 * negative (a stop that still risks a little), but never back to or past the original stop.
 */
export type BreakevenSettings = {
  triggerTicks: number;
  offsetTicks: number;
  /** As typed in the preset's unit (ADR-0049). */
  triggerEntered?: string;
  offsetEntered?: string;
};

export type BracketPreset = {
  id: string;
  name: string;
  /**
   * The unit the template is written in (ADR-0049). "ticks": the ticks are the truth. "points" /
   * "dollars" ($ per contract): every offset's `…Entered` value is the truth and its ticks are
   * re-derived for the contract traded (`resolvePreset`); the stored ticks are those of the
   * contract the editor previewed when it was saved. Older documents load as "ticks".
   */
  unit: OffsetUnit;
  /** Default order quantity; must equal the sum of the legs' qty. */
  qty: number;
  legs: BracketLeg[];
  trail: TrailSettings | null;
  breakeven: BreakevenSettings | null;
  /** Roots the preset is offered for ("NQ", "MNQ" …), or "all". */
  symbols: "all" | string[];
  /** A seeded example the user may edit or delete. */
  example: boolean;
};

export type BracketDoc = {
  v: typeof BRACKET_DOC_VERSION;
  presets: BracketPreset[];
  /** Last choice per root: a preset id or NO_BRACKET. Missing = never chosen (shows "No bracket"). */
  selected: Record<string, string>;
};

// ---- contracts -------------------------------------------------------------------------------

/**
 * Tick size as a rational (points per tick = num/den) and tick value in integer cents. `live`:
 * from the instrument's reference data (`InstrumentInfo`), not the CME table below — a points or
 * $ template is only ever sized from live facts (ADR-0049: never guess a point value).
 */
export type ContractFacts = {
  tickSizeNum: number;
  tickSizeDen: number;
  tickValueCents: number;
  live?: boolean;
};

/**
 * CME contract specs for the preview when `InstrumentInfo` has not arrived (the live md always
 * wins). Tick value = tick size × multiplier.
 */
export const FALLBACK_CONTRACTS: Readonly<Record<string, ContractFacts>> = {
  NQ: { tickSizeNum: 1, tickSizeDen: 4, tickValueCents: 500 }, // 0.25 × $20
  MNQ: { tickSizeNum: 1, tickSizeDen: 4, tickValueCents: 50 }, // 0.25 × $2
  ES: { tickSizeNum: 1, tickSizeDen: 4, tickValueCents: 1250 }, // 0.25 × $50
  MES: { tickSizeNum: 1, tickSizeDen: 4, tickValueCents: 125 }, // 0.25 × $5
  RTY: { tickSizeNum: 1, tickSizeDen: 10, tickValueCents: 500 }, // 0.1 × $50
  M2K: { tickSizeNum: 1, tickSizeDen: 10, tickValueCents: 50 }, // 0.1 × $5
  YM: { tickSizeNum: 1, tickSizeDen: 1, tickValueCents: 500 }, // 1 × $5
  MYM: { tickSizeNum: 1, tickSizeDen: 1, tickValueCents: 50 }, // 1 × $0.50
  CL: { tickSizeNum: 1, tickSizeDen: 100, tickValueCents: 1000 }, // 0.01 × $1,000
  MCL: { tickSizeNum: 1, tickSizeDen: 100, tickValueCents: 100 }, // 0.01 × $100
  GC: { tickSizeNum: 1, tickSizeDen: 10, tickValueCents: 1000 }, // 0.1 × $100
  MGC: { tickSizeNum: 1, tickSizeDen: 10, tickValueCents: 100 }, // 0.1 × $10
};

/** "NQZ6" / "NQZ26" / "NQ" → "NQ". */
export function symbolRoot(symbol: string): string {
  const s = symbol.trim().toUpperCase();
  const m = /^([A-Z0-9]+?)[FGHJKMNQUVXZ]\d{1,2}$/.exec(s);
  return m?.[1] ?? s;
}

export function contractFactsFor(symbol: string | null): ContractFacts | null {
  if (symbol === null) return null;
  return FALLBACK_CONTRACTS[symbolRoot(symbol)] ?? null;
}

// ---- unit conversion (exact) -----------------------------------------------------------------

export type OffsetUnit = "ticks" | "points" | "dollars";
/** A $ amount as integer cents ("62.5" → 6250); null when it is not a plain amount above $0. */
export function parseCents(input: string): number | null {
  const p = parseDecimal(input);
  if (!p || p.d > 100n) return null;
  const cents = (p.n * 100n) / p.d;
  return cents > 0n && cents <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(cents) : null;
}

/**
 * Older $ legs across a leg's quantity (Q-B1, SPEC §9.4): stops round toward less risk, targets
 * toward at least the entered profit. Template units (ADR-0049) round to the nearest tick instead.
 */
export type OffsetRole = "stop" | "target";

export type TicksResult = { ok: true; ticks: number } | { ok: false; message: string };

/** Parse a plain non-negative decimal ("12", "3.25", ".5") into an exact rational n / 10^d. */
function parseDecimal(input: string): { n: bigint; d: bigint } | null {
  const s = input.trim().replace(/[$,\s]/g, "");
  const m = /^(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || s === "" || s === ".") return null;
  const frac = m[2] ?? "";
  if (frac.length > 8) return null;
  return { n: BigInt(`${m[1] ?? ""}${frac}` || "0"), d: 10n ** BigInt(frac.length) };
}

function checkTicks(t: bigint): TicksResult {
  if (t <= 0n) return { ok: false, message: "Must be at least 1 tick" };
  if (t > BigInt(MAX_OFFSET_TICKS))
    return { ok: false, message: `At most ${String(MAX_OFFSET_TICKS)} ticks` };
  return { ok: true, ticks: Number(t) };
}

/** Whole ticks: "12" → 12. */
export function parseTicks(input: string): TicksResult {
  const s = input.trim();
  if (!/^\d+$/.test(s)) return { ok: false, message: "Enter a whole number of ticks" };
  return checkTicks(BigInt(s));
}

/**
 * Points → ticks, exact: points / (num/den) must be a whole number of ticks (NQ 3.25 → 13; NQ 3.1
 * is rejected, not rounded, because it is not on the tick grid).
 */
export function pointsToTicks(input: string, c: ContractFacts): TicksResult {
  const p = parseDecimal(input);
  if (!p) return { ok: false, message: "Enter points, e.g. 3.25" };
  if (c.tickSizeNum <= 0 || c.tickSizeDen <= 0) return { ok: false, message: "Tick size unknown" };
  const top = p.n * BigInt(c.tickSizeDen);
  const bottom = p.d * BigInt(c.tickSizeNum);
  if (top % bottom !== 0n) {
    return { ok: false, message: `Not a whole number of ticks (tick ${ticksToPoints(1, c)})` };
  }
  return checkTicks(top / bottom);
}

/**
 * $ for one leg (per SPEC §9.4, across that leg's `qty`) → ticks. Stop: floor, so the loss never
 * exceeds the amount ($300 on 7 MNQ → 85 ticks = $297.50); floors to 0 → rejected. Target: ceil,
 * so the profit reaches at least the amount.
 */
export function dollarsToTicks(input: string, qty: number, c: ContractFacts, role: OffsetRole): TicksResult {
  const p = parseDecimal(input);
  if (!p || p.d > 100n) return { ok: false, message: "Enter dollars, e.g. 150 or 62.50" };
  if (c.tickValueCents <= 0) return { ok: false, message: "Tick value unknown" };
  if (!Number.isSafeInteger(qty) || qty < 1) return { ok: false, message: "Quantity must be at least 1" };
  const cents = (p.n * 100n) / p.d; // exact: at most 2 decimals
  if (cents <= 0n) return { ok: false, message: "Enter an amount above $0" };
  const perTick = BigInt(c.tickValueCents) * BigInt(qty);
  const floor = cents / perTick;
  if (role === "stop") {
    if (floor === 0n) return { ok: false, message: "Risk $ is below 1 tick at this quantity" };
    return checkTicks(floor);
  }
  return checkTicks(cents % perTick === 0n ? floor : floor + 1n);
}

/** Convert an entered offset in any unit to ticks. */
export function toTicks(
  input: string,
  unit: OffsetUnit,
  role: OffsetRole,
  qty: number,
  c: ContractFacts | null,
): TicksResult {
  if (unit === "ticks") return parseTicks(input);
  if (c === null) return { ok: false, message: "Pick a contract to enter points or $" };
  return unit === "points" ? pointsToTicks(input, c) : dollarsToTicks(input, qty, c, role);
}

/** Ticks → exact points string ("13" → "3.25"); non-terminating tick sizes round to 8 places. */
export function ticksToPoints(ticks: number, c: ContractFacts): string {
  const num = BigInt(ticks) * BigInt(c.tickSizeNum);
  const den = BigInt(c.tickSizeDen);
  const neg = num < 0n;
  const a = neg ? -num : num;
  const whole = a / den;
  let rem = a % den;
  let frac = "";
  for (let i = 0; rem !== 0n && i < 8; i++) {
    rem *= 10n;
    frac += (rem / den).toString();
    rem %= den;
  }
  return `${neg ? "-" : ""}${whole.toString()}${frac ? `.${frac}` : ""}`;
}

/**
 * A leg with its target or stop set to `ticks`. `cents` is the $ amount the ticks came from (the
 * editor in $), kept so the order re-derives them for its quantity (Q-B1); null clears it.
 */
export function withLegOffset(
  l: BracketLeg,
  role: OffsetRole,
  ticks: number,
  cents: number | null,
): BracketLeg {
  const { targetCents, stopCents, ...rest } = l;
  const next: BracketLeg =
    role === "target" ? { ...rest, targetTicks: ticks } : { ...rest, stopTicks: ticks };
  const t = role === "target" ? cents : (targetCents ?? null);
  const s = role === "stop" ? cents : (stopCents ?? null);
  if (t !== null) next.targetCents = t;
  if (s !== null) next.stopCents = s;
  return next;
}

/** Ticks × tick value × qty, integer cents. */
export function ticksToCents(ticks: number, qty: number, c: ContractFacts): number {
  return ticks * c.tickValueCents * qty;
}

/** Cents → the plain input form "62.5" → "62.50", "150.00" → "150", −50 → "-0.50". */
export function centsToInputString(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const a = Math.abs(cents);
  const whole = Math.trunc(a / 100);
  const rem = a % 100;
  return `${sign}${String(whole)}${rem === 0 ? "" : `.${String(rem).padStart(2, "0")}`}`;
}

/** A tick offset as the editor shows it in `unit` (for re-filling the field on a unit switch). */
export function formatOffset(ticks: number, unit: OffsetUnit, qty: number, c: ContractFacts | null): string {
  if (unit === "ticks" || c === null) return String(ticks);
  if (unit === "points") return ticksToPoints(ticks, c);
  return centsToInputString(ticksToCents(ticks, qty, c));
}

// ---- template units (ADR-0049) ---------------------------------------------------------------

export const OFFSET_UNITS: readonly OffsetUnit[] = ["ticks", "points", "dollars"];

/** What a template field is; it decides the rounding and the allowed range. */
export type FieldRole =
  "target" | "stop" | "beTrigger" | "beOffset" | "trailTrigger" | "trailDistance" | "trailStep";

/** How a value off the tick grid becomes whole ticks: the nearest tick, halves away from zero. */
export type Rounding = "nearest";

/**
 * Owner revision 2026-10-04: every field rounds to the nearest tick, halves away from zero — the
 * stop, the trail distance and the breakeven offset as much as the target, the triggers and the
 * trail step (the 2026-10-03 "toward the entry" rule for risk fields is withdrawn: on NQ / MNQ the
 * half-tick difference is negligible and the owner wants the stop where it was meant). Kept per
 * field so a later per-field rule stays a one-line change.
 */
export function roundingFor(_role: FieldRole): Rounding {
  return "nearest";
}

/**
 * A typed value as whole ticks. `exact` is false when the value was off the tick grid and was
 * rounded per `rounding`; the UI must say so next to the value before anything is sent.
 */
export type Converted =
  { ok: true; ticks: number; exact: boolean; rounding: Rounding } | { ok: false; message: string };

/** "$5.00" from integer cents (messages only; the UI formats with `formatCents`). */
function usd(cents: number): string {
  const neg = cents < 0;
  const a = Math.abs(cents);
  return `${neg ? "-" : ""}$${String(Math.trunc(a / 100))}.${String(a % 100).padStart(2, "0")}`;
}

/** A plain decimal with an optional leading minus: exact rational n / d (d = 10^k). */
function parseSignedDecimal(input: string, allowNegative: boolean): { n: bigint; d: bigint } | null {
  const s = input.trim().replace(/−/g, "-");
  const neg = s.startsWith("-");
  if (neg && !allowNegative) return null;
  const p = parseDecimal(neg ? s.slice(1) : s);
  if (!p) return null;
  return { n: neg ? -p.n : p.n, d: p.d };
}

/**
 * A value typed in `unit` → whole ticks for contract `c`, exactly (bigint rationals, no floats):
 * points ÷ tick size, $ per contract ÷ tick value. A value off the tick grid is rounded per
 * `roundingFor(role)` (nearest, halves away from zero) and reported as not exact: ES $37 = 2.96
 * ticks → 3 ($37.50); NQ $37 = 7.4 → 7 ($35.00); NQ $37.50 = 7.5 → 8; a −7.5 tick breakeven
 * offset → −8. Ticks must be typed whole. Only the breakeven offset may be negative or zero.
 */
export function enteredToTicks(
  input: string,
  unit: OffsetUnit,
  role: FieldRole,
  c: ContractFacts | null,
): Converted {
  const signed = role === "beOffset";
  const p = parseSignedDecimal(input, signed);
  if (p === null) {
    const what = unit === "ticks" ? "ticks" : unit === "points" ? "points" : "dollars";
    return { ok: false, message: `Enter ${what}${signed ? "" : " above 0"}` };
  }
  let top: bigint;
  let bottom: bigint;
  if (unit === "ticks") {
    if (p.d !== 1n) return { ok: false, message: "Enter a whole number of ticks" };
    top = p.n;
    bottom = 1n;
  } else if (c === null || c.tickSizeNum <= 0 || c.tickSizeDen <= 0) {
    return { ok: false, message: "Tick size unknown" };
  } else if (unit === "points") {
    top = p.n * BigInt(c.tickSizeDen);
    bottom = p.d * BigInt(c.tickSizeNum);
  } else {
    if (p.d > 100n) return { ok: false, message: "Dollars have at most 2 decimals" };
    if (c.tickValueCents <= 0) return { ok: false, message: "Tick value unknown" };
    top = (p.n * 100n) / p.d; // exact: at most 2 decimals
    bottom = BigInt(c.tickValueCents);
  }
  const rounding = roundingFor(role);
  const t = roundHalfAway(top, bottom);
  const exact = top % bottom === 0n;
  const oneTick =
    c === null || unit === "ticks"
      ? "1 tick"
      : `1 tick (${ticksToPoints(1, c)} pt = ${usd(c.tickValueCents)} per contract)`;
  if (signed) {
    if (t > BigInt(MAX_OFFSET_TICKS) || t < -BigInt(MAX_OFFSET_TICKS))
      return { ok: false, message: `At most ${String(MAX_OFFSET_TICKS)} ticks either way` };
    return { ok: true, ticks: Number(t), exact, rounding };
  }
  if (p.n <= 0n) return { ok: false, message: "Must be above 0" };
  if (t <= 0n) return { ok: false, message: `Rounds to 0 ticks; the minimum is ${oneTick}: not sendable` };
  if (t > BigInt(MAX_OFFSET_TICKS))
    return { ok: false, message: `At most ${String(MAX_OFFSET_TICKS)} ticks` };
  return { ok: true, ticks: Number(t), exact, rounding };
}

/** A value as typed, with its unit: "$37", "-$2.50", "5.1 pts", "12t". */
export function enteredLabel(entered: string, unit: OffsetUnit): string {
  const v = entered.trim().replace(/−/g, "-");
  if (unit === "dollars") return v.startsWith("-") ? `-$${v.slice(1)}` : `$${v}`;
  return unit === "points" ? `${v} pts` : `${v}t`;
}

/** One offset of a template, addressed by the same path validation uses. */
export type PresetField = {
  path: string;
  role: FieldRole;
  label: string;
  ticks: number | null;
  entered: string | undefined;
};

/** Every offset the template places (absent legs' targets / stops and off settings are skipped). */
export function presetFields(p: BracketPreset): PresetField[] {
  const out: PresetField[] = [];
  const add = (
    path: string,
    role: FieldRole,
    label: string,
    ticks: number | null,
    entered: string | undefined,
  ): void => {
    out.push({ path, role, label, ticks, entered });
  };
  const many = p.legs.length > 1;
  p.legs.forEach((l, i) => {
    const n = many ? ` ${String(i + 1)}` : "";
    const at = `legs.${String(i)}.`;
    if (l.targetTicks !== null)
      add(`${at}targetTicks`, "target", `Target${n}`, l.targetTicks, l.targetEntered);
    if (l.stopTicks !== null) add(`${at}stopTicks`, "stop", `Stop${n}`, l.stopTicks, l.stopEntered);
  });
  const b = p.breakeven;
  if (b) {
    add("breakeven.triggerTicks", "beTrigger", "Breakeven trigger", b.triggerTicks, b.triggerEntered);
    add("breakeven.offsetTicks", "beOffset", "Breakeven offset", b.offsetTicks, b.offsetEntered);
  }
  const t = p.trail;
  if (t) {
    add("trail.triggerTicks", "trailTrigger", "Trail trigger", t.triggerTicks, t.triggerEntered);
    add("trail.distanceTicks", "trailDistance", "Trail distance", t.distanceTicks, t.distanceEntered);
    add("trail.stepTicks", "trailStep", "Trail step", t.stepTicks, t.stepEntered);
  }
  return out;
}

/** The offsets a template field path may end in; each has a typed value `<name>Entered`. */
const OFFSET_KEY = /^(target|stop|trigger|offset|distance|step)Ticks$/;
const enteredKey = (key: string): string => key.replace(/Ticks$/, "Entered");

/**
 * The template with the field at `path` set to `ticks`, and its typed value to `entered`
 * (undefined removes it). A leg's older $ amount for that side (Q-B1) is dropped: the field was
 * typed again. Unknown paths leave the template as it is.
 */
export function withField(
  p: BracketPreset,
  path: string,
  ticks: number,
  entered: string | undefined,
): BracketPreset {
  const parts = path.split(".");
  const key = parts.at(-1) ?? "";
  if (!OFFSET_KEY.test(key)) return p;
  const ek = enteredKey(key);
  const drop = new Set([ek, key === "targetTicks" ? "targetCents" : key === "stopTicks" ? "stopCents" : ""]);
  const set = <T extends object>(o: T): T => {
    const next: Record<string, unknown> = Object.fromEntries(
      Object.entries({ ...o, [key]: ticks }).filter(([k]) => !drop.has(k)),
    );
    if (entered !== undefined) next[ek] = entered;
    return next as T;
  };
  if (parts[0] === "legs") {
    const i = Number(parts[1]);
    if (!p.legs[i]) return p;
    return { ...p, legs: p.legs.map((l, j) => (j === i ? set(l) : l)) };
  }
  if (parts[0] === "breakeven" && p.breakeven) return { ...p, breakeven: set(p.breakeven) };
  if (parts[0] === "trail" && p.trail) return { ...p, trail: set(p.trail) };
  return p;
}

/** One field's typed value → ticks for `c`, for display next to the field and before sending. */
export type FieldConversion = {
  path: string;
  role: FieldRole;
  label: string;
  unit: OffsetUnit;
  entered: string;
  result: Converted;
};

/**
 * The template's ticks for contract `c`: in points / $ every field is re-derived from its typed
 * value (NaN where it cannot be — the conversion says why); in ticks nothing changes. Pure, no
 * check that `c` is live (the editor previews from the CME table; `resolvePreset` sends).
 */
export function derivePreset(
  p: BracketPreset,
  c: ContractFacts | null,
): { preset: BracketPreset; conversions: FieldConversion[] } {
  if (p.unit === "ticks") return { preset: p, conversions: [] };
  let out = p;
  const conversions: FieldConversion[] = [];
  for (const f of presetFields(p)) {
    const result: Converted =
      f.entered === undefined
        ? { ok: false, message: "No value entered" }
        : enteredToTicks(f.entered, p.unit, f.role, c);
    conversions.push({
      path: f.path,
      role: f.role,
      label: f.label,
      unit: p.unit,
      entered: f.entered ?? "",
      result,
    });
    out = withField(out, f.path, result.ok ? result.ticks : Number.NaN, f.entered);
  }
  return { preset: out, conversions };
}

export type Resolved =
  { ok: true; preset: BracketPreset; conversions: FieldConversion[] } | { ok: false; message: string };

/** Why a points / $ template cannot be sized without the instrument's reference data. */
export function unitUnavailableMessage(unit: OffsetUnit): string {
  return unit === "dollars"
    ? "$ per contract unavailable until reference data loads"
    : "Points unavailable until reference data loads";
}

/**
 * The template as it is sent for the contract with facts `c` (ADR-0049): a ticks template as
 * stored; a points / $ template only from **live** reference data (never the CME table, never a
 * guess), every field re-derived to the nearest tick (shown first), and the result must pass every
 * preset rule.
 */
export function resolvePreset(p: BracketPreset, c: ContractFacts | null): Resolved {
  if (p.unit !== "ticks" && c?.live !== true) return { ok: false, message: unitUnavailableMessage(p.unit) };
  const d = derivePreset(p, c);
  for (const x of d.conversions) {
    if (!x.result.ok)
      return { ok: false, message: `${x.label} ${enteredLabel(x.entered, x.unit)}: ${x.result.message}` };
  }
  const issue = validatePreset(d.preset)[0];
  if (issue) return { ok: false, message: issue.message };
  return { ok: true, preset: d.preset, conversions: d.conversions };
}

/** The template without any typed values (older $ amounts, Q-B1, are kept). */
function stripEntered(p: BracketPreset): BracketPreset {
  const strip = <T extends object>(o: T): T =>
    Object.fromEntries(Object.entries(o).filter(([k]) => !k.endsWith("Entered"))) as T;
  return {
    ...p,
    legs: p.legs.map(strip),
    trail: p.trail && strip(p.trail),
    breakeven: p.breakeven && strip(p.breakeven),
  };
}

// ---- validation ------------------------------------------------------------------------------

export type PresetIssue = { path: string; message: string };

const isPosInt = (n: unknown, max: number): n is number =>
  typeof n === "number" && Number.isSafeInteger(n) && n >= 1 && n <= max;

export function cleanPresetName(name: string): string {
  return name.replace(/\s+/g, " ").trim().slice(0, MAX_PRESET_NAME).trim();
}

/**
 * Every rule a preset must pass before it is saved. Paths name the field (`name`, `qty`,
 * `legs.1.stopTicks`, `trail.stepTicks` …) so the editor can mark it.
 */
export function validatePreset(p: BracketPreset, others: readonly BracketPreset[] = []): PresetIssue[] {
  const out: PresetIssue[] = [];
  const name = cleanPresetName(p.name);
  if (name === "") out.push({ path: "name", message: "Name the strategy" });
  else if (others.some((o) => o.id !== p.id && o.name.toLowerCase() === name.toLowerCase()))
    out.push({ path: "name", message: "Another strategy has this name" });
  if (!isPosInt(p.qty, MAX_PRESET_QTY))
    out.push({ path: "qty", message: `Quantity must be 1–${String(MAX_PRESET_QTY)}` });
  if (p.legs.length < 1) out.push({ path: "legs", message: "Add at least one leg" });
  if (p.legs.length > MAX_LEGS) out.push({ path: "legs", message: `At most ${String(MAX_LEGS)} legs` });
  if (!OFFSET_UNITS.includes(p.unit)) out.push({ path: "unit", message: "Pick ticks, points or $" });
  let sum = 0;
  p.legs.forEach((l, i) => {
    if (!isPosInt(l.qty, MAX_PRESET_QTY))
      out.push({ path: `legs.${String(i)}.qty`, message: "Leg quantity must be at least 1" });
    else sum += l.qty;
    if (l.targetTicks !== null && !isPosInt(l.targetTicks, MAX_OFFSET_TICKS))
      out.push({ path: `legs.${String(i)}.targetTicks`, message: "Profit target must be 1 tick or more" });
    if (l.stopTicks !== null && !isPosInt(l.stopTicks, MAX_OFFSET_TICKS))
      out.push({ path: `legs.${String(i)}.stopTicks`, message: "Stop loss must be 1 tick or more" });
    for (const k of ["targetCents", "stopCents"] as const) {
      const c = l[k];
      if (c !== undefined && !isPosInt(c, Number.MAX_SAFE_INTEGER))
        out.push({ path: `legs.${String(i)}.${k}`, message: "Amount must be above $0" });
    }
  });
  if (p.legs.some((l) => l.targetTicks === null && l.stopTicks === null))
    out.push({ path: "exits", message: "A template needs a profit target, a stop loss or both" });
  if (p.legs.length > 0 && isPosInt(p.qty, MAX_PRESET_QTY) && sum !== p.qty)
    out.push({
      path: "legs",
      message: `Leg quantities add up to ${String(sum)}, the strategy quantity is ${String(p.qty)}`,
    });
  const stops = p.legs.flatMap((l) => (l.stopTicks === null ? [] : [l.stopTicks]));
  if (p.trail) {
    // The gateway trails a stop; without one there is nothing to move (BracketPlan::from_pb).
    if (stops.length === 0)
      out.push({ path: "trail.triggerTicks", message: "Auto trail needs a stop loss to move" });
    if (!isPosInt(p.trail.triggerTicks, MAX_OFFSET_TICKS))
      out.push({ path: "trail.triggerTicks", message: "Trail trigger must be 1 tick or more" });
    if (!isPosInt(p.trail.distanceTicks, MAX_OFFSET_TICKS))
      out.push({ path: "trail.distanceTicks", message: "Trail distance must be 1 tick or more" });
    if (!isPosInt(p.trail.stepTicks, MAX_OFFSET_TICKS))
      out.push({ path: "trail.stepTicks", message: "Trail step must be 1 tick or more" });
  }
  if (p.breakeven) {
    const b = p.breakeven;
    if (stops.length === 0)
      out.push({ path: "breakeven.triggerTicks", message: "Auto breakeven needs a stop loss to move" });
    if (!isPosInt(b.triggerTicks, MAX_OFFSET_TICKS))
      out.push({ path: "breakeven.triggerTicks", message: "Breakeven trigger must be 1 tick or more" });
    const minStop = Math.min(...stops);
    if (typeof b.offsetTicks !== "number" || !Number.isSafeInteger(b.offsetTicks))
      out.push({ path: "breakeven.offsetTicks", message: "Breakeven offset must be whole ticks" });
    else if (stops.length > 0 && Number.isFinite(minStop) && b.offsetTicks <= -minStop)
      // Moving the stop back to (or behind) where it started is not a breakeven.
      out.push({ path: "breakeven.offsetTicks", message: "Offset must stay above the original stop" });
    else if (isPosInt(b.triggerTicks, MAX_OFFSET_TICKS) && b.offsetTicks >= b.triggerTicks)
      // The moved stop would sit at or through the price that triggered it and fill at once.
      out.push({ path: "breakeven.offsetTicks", message: "Offset must be below the trigger" });
  }
  if (p.symbols !== "all") {
    if (p.symbols.length === 0) out.push({ path: "symbols", message: "Pick at least one symbol, or All" });
    else if (p.symbols.some((s) => !/^[A-Z0-9]{1,6}$/.test(s)))
      out.push({ path: "symbols", message: "Symbols are roots like NQ or MES" });
  }
  // A points / $ template sends what was typed, re-derived per contract: every offset needs it.
  if (p.unit !== "ticks") {
    for (const f of presetFields(p)) {
      if (f.entered === undefined || parseSignedDecimal(f.entered, f.role === "beOffset") === null)
        out.push({ path: f.path, message: "Enter a value" });
    }
  }
  return out;
}

// ---- prices and summaries --------------------------------------------------------------------

export type BracketSide = "buy" | "sell";
/** Absolute prices in ticks; null where the leg places no target / no stop. */
export type LegPrices = { qty: number; targetTicks: number | null; stopTicks: number | null };

/**
 * Absolute prices (in ticks) of each leg's target and stop for an entry at `entryTicks`: a buy's
 * target is above the entry and its stop below; a sell's the other way round.
 */
export function bracketPrices(p: BracketPreset, side: BracketSide, entryTicks: number): LegPrices[] {
  const dir = side === "buy" ? 1 : -1;
  return p.legs.map((l) => ({
    qty: l.qty,
    targetTicks: l.targetTicks === null ? null : entryTicks + dir * l.targetTicks,
    stopTicks: l.stopTicks === null ? null : entryTicks - dir * l.stopTicks,
  }));
}

/** Legs as placed: a preset's, or an order's after sizing (`OrderBracketLeg`). */
export type SizedLeg = { qty: number; targetTicks: number | null; stopTicks: number | null };

/**
 * Total $ at stake / to gain if every leg hits its stop / target (integer cents, each leg at its
 * own quantity). `riskCents` is null when some leg has no stop — the loss is not bounded, and the
 * UI says "no stop" rather than $0; `rewardCents` likewise without a target.
 */
export function legsRiskReward(
  legs: readonly SizedLeg[],
  c: ContractFacts,
): { riskCents: number | null; rewardCents: number | null } {
  let risk: number | null = 0;
  let reward: number | null = 0;
  for (const l of legs) {
    risk = risk === null || l.stopTicks === null ? null : risk + ticksToCents(l.stopTicks, l.qty, c);
    reward =
      reward === null || l.targetTicks === null ? null : reward + ticksToCents(l.targetTicks, l.qty, c);
  }
  return { riskCents: risk, rewardCents: reward };
}

const MINUS = "−";
const uniq = (xs: number[]): number[] => [...new Set(xs)];

/**
 * "TP +12t / SL −8t · 2 legs · BE · Trail" (distinct leg values joined with "/"); a side no leg
 * places reads "no TP" / "no SL" (ADR-0049), one only some legs place gets "(some legs)".
 */
export function presetSummary(p: BracketPreset): string {
  const side = (vals: (number | null)[], name: string, sign: string): string => {
    const set = uniq(vals.flatMap((v) => (v === null ? [] : [v])));
    if (set.length === 0) return `no ${name}`;
    const some = vals.some((v) => v === null) ? " (some legs)" : "";
    return `${name} ${set.map((t) => `${sign}${String(t)}`).join("/")}t${some}`;
  };
  const tp = side(
    p.legs.map((l) => l.targetTicks),
    "TP",
    "+",
  );
  const sl = side(
    p.legs.map((l) => l.stopTicks),
    "SL",
    MINUS,
  );
  const parts = [`${tp} / ${sl}`];
  parts.push(p.legs.length === 1 ? `${String(p.qty)} ct` : `${String(p.legs.length)} legs`);
  if (p.breakeven) parts.push("BE");
  if (p.trail) parts.push("Trail");
  return parts.join(" · ");
}

export function presetAppliesTo(p: BracketPreset, symbol: string | null): boolean {
  if (p.symbols === "all" || symbol === null) return true;
  return p.symbols.includes(symbolRoot(symbol));
}

/** Presets offered for `symbol` in the dropdown, in the user's order. */
export function presetsFor(doc: BracketDoc, symbol: string | null): BracketPreset[] {
  return doc.presets.filter((p) => presetAppliesTo(p, symbol));
}

/**
 * The remembered choice for `symbol`: the preset, or null for "No bracket" (explicit, never
 * chosen, deleted, or no longer offered for this symbol).
 */
export function selectedPreset(doc: BracketDoc, symbol: string | null): BracketPreset | null {
  if (symbol === null) return null;
  const id = doc.selected[symbolRoot(symbol)];
  if (id === undefined || id === NO_BRACKET) return null;
  const p = doc.presets.find((x) => x.id === id);
  return p && presetAppliesTo(p, symbol) ? p : null;
}

// ---- document edits (pure) -------------------------------------------------------------------

export function selectPreset(doc: BracketDoc, symbol: string, id: string | null): BracketDoc {
  const root = symbolRoot(symbol);
  const value = id ?? NO_BRACKET;
  if (doc.selected[root] === value) return doc;
  return { ...doc, selected: { ...doc.selected, [root]: value } };
}

// ---- starter set (SPEC §9.4 "Default presets seeded per root") ---------------------------------

const leg = (qty: number, targetTicks: number, stopTicks: number): BracketLeg => ({
  qty,
  targetTicks,
  stopTicks,
});

/** Examples the user can edit or delete; SPEC §9.4's per-root table, plus Runner as a 2-leg scale-out. */
export function starterPresets(): BracketPreset[] {
  const base = { unit: "ticks", trail: null, breakeven: null, example: true } as const;
  return [
    {
      ...base,
      id: "ex-nq-scalp",
      name: "NQ Scalp 16/12",
      qty: 1,
      legs: [leg(1, 16, 12)],
      symbols: ["NQ", "MNQ"],
    },
    {
      ...base,
      id: "ex-nq-1to2",
      name: "NQ 1:2 20/10",
      qty: 1,
      legs: [leg(1, 20, 10)],
      symbols: ["NQ", "MNQ"],
    },
    {
      ...base,
      id: "ex-nq-runner",
      name: "NQ Runner (2-leg scale-out)",
      qty: 2,
      legs: [leg(1, 16, 16), leg(1, 40, 16)],
      breakeven: { triggerTicks: 16, offsetTicks: 1 },
      symbols: ["NQ", "MNQ"],
    },
    {
      ...base,
      id: "ex-es-scalp",
      name: "ES Scalp 6/4",
      qty: 1,
      legs: [leg(1, 6, 4)],
      symbols: ["ES", "MES"],
    },
    { ...base, id: "ex-es-1to2", name: "ES 1:2 8/4", qty: 1, legs: [leg(1, 8, 4)], symbols: ["ES", "MES"] },
    {
      ...base,
      id: "ex-es-runner",
      name: "ES Runner (2-leg scale-out)",
      qty: 2,
      legs: [leg(1, 6, 6), leg(1, 16, 6)],
      breakeven: { triggerTicks: 6, offsetTicks: 1 },
      symbols: ["ES", "MES"],
    },
  ];
}

export function defaultBracketDoc(): BracketDoc {
  return { v: BRACKET_DOC_VERSION, presets: starterPresets(), selected: {} };
}

// ---- sanitising (documents from storage / the server / other versions) -----------------------

/** A leg side's ticks: a positive whole number, or null (explicitly absent — ADR-0049). */
const isSide = (v: unknown): v is number | null => v === null || isPosInt(v, MAX_OFFSET_TICKS);
const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);

function sanitizeLeg(raw: unknown): BracketLeg | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const l = { qty: r["qty"], targetTicks: r["targetTicks"], stopTicks: r["stopTicks"] };
  if (!isPosInt(l.qty, MAX_PRESET_QTY) || !isSide(l.targetTicks) || !isSide(l.stopTicks)) return null;
  const leg: BracketLeg = { qty: l.qty, targetTicks: l.targetTicks, stopTicks: l.stopTicks };
  // $ amounts are optional; a malformed one is dropped (the ticks still stand).
  const tc = r["targetCents"];
  const sc = r["stopCents"];
  if (isPosInt(tc, Number.MAX_SAFE_INTEGER) && leg.targetTicks !== null) leg.targetCents = tc;
  if (isPosInt(sc, Number.MAX_SAFE_INTEGER) && leg.stopTicks !== null) leg.stopCents = sc;
  const te = str(r["targetEntered"]);
  const se = str(r["stopEntered"]);
  if (te !== undefined && leg.targetTicks !== null) leg.targetEntered = te;
  if (se !== undefined && leg.stopTicks !== null) leg.stopEntered = se;
  return leg;
}

/** `o` plus, for each of its `<name>Ticks` keys, `<name>Entered` from `raw` when it is a string. */
function withEnteredFrom<T extends object>(o: T, raw: Record<string, unknown>): T {
  const out = { ...o } as Record<string, unknown>;
  for (const k of Object.keys(o)) {
    const v = raw[enteredKey(k)];
    if (OFFSET_KEY.test(k) && typeof v === "string") out[enteredKey(k)] = v;
  }
  return out as T;
}

function sanitizeTrail(tr: Record<string, unknown>): TrailSettings {
  const t: TrailSettings = {
    triggerTicks: Number(tr["triggerTicks"]),
    distanceTicks: Number(tr["distanceTicks"]),
    stepTicks: Number(tr["stepTicks"]),
  };
  return withEnteredFrom(t, tr);
}

function sanitizeBreakeven(be: Record<string, unknown>): BreakevenSettings {
  const b: BreakevenSettings = {
    triggerTicks: Number(be["triggerTicks"]),
    offsetTicks: Number(be["offsetTicks"]),
  };
  return withEnteredFrom(b, be);
}

function sanitizePreset(raw: unknown): BracketPreset | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r["id"] !== "string" || r["id"] === "" || typeof r["name"] !== "string") return null;
  if (!Array.isArray(r["legs"])) return null;
  const legs = r["legs"].map(sanitizeLeg);
  if (legs.some((l) => l === null)) return null;
  const tr = r["trail"] as Record<string, unknown> | null | undefined;
  const be = r["breakeven"] as Record<string, unknown> | null | undefined;
  const syms = r["symbols"];
  const rawUnit = r["unit"];
  // Documents from before ADR-0049 have no unit: they are ticks templates.
  const unit: OffsetUnit = OFFSET_UNITS.find((u) => u === rawUnit) ?? "ticks";
  let p: BracketPreset = {
    id: r["id"],
    name: cleanPresetName(r["name"]),
    unit,
    qty: typeof r["qty"] === "number" ? r["qty"] : Number.NaN,
    legs: legs as BracketLeg[],
    trail: tr && typeof tr === "object" ? sanitizeTrail(tr) : null,
    breakeven: be && typeof be === "object" ? sanitizeBreakeven(be) : null,
    symbols:
      syms === "all"
        ? "all"
        : Array.isArray(syms)
          ? syms.filter((s): s is string => typeof s === "string").map((s) => s.toUpperCase())
          : "all",
    example: r["example"] === true,
  };
  if (p.unit === "ticks") {
    // Typed values only mean something in points / $; a ticks template's ticks are the truth.
    p = stripEntered(p);
  } else {
    // Older $ amounts (Q-B1) only apply to ticks templates.
    p = { ...p, legs: p.legs.map(({ targetCents: _t, stopCents: _s, ...l }) => l) };
    // A points / $ template missing a typed value cannot be re-derived: keep its stored ticks as
    // a ticks template rather than drop it (an older client may have stripped the values).
    const missing = presetFields(p).some(
      (f) => f.entered === undefined || parseSignedDecimal(f.entered, f.role === "beOffset") === null,
    );
    if (missing) p = { ...stripEntered(p), unit: "ticks" };
  }
  // A preset that no longer validates (hand-edited, older rules) is dropped, not half-applied.
  return validatePreset(p).length === 0 ? p : null;
}

// ---- stored form: older builds never see a contract-dependent template (ADR-0049) -----------

/**
 * The synced / cached document. `presets` keeps the shape every older build reads, and holds only
 * templates those builds send exactly as this one does (`legacyCompatible`). Every other template
 * — points or $ per contract (its ticks depend on the contract traded) and target-only / stop-only
 * — lives in `templates_v2`, a field older builds do not read, so they can never send it as raw
 * ticks; they simply do not offer it (a selection pointing at it shows "No bracket"). `order` keeps
 * the user's order across both lists. `bracket_presets` is field-merged per top-level key (§1A.3).
 */
export type StoredBracketDoc = {
  v: typeof BRACKET_DOC_VERSION;
  presets: Record<string, unknown>[];
  templates_v2: BracketPreset[];
  order: string[];
  selected: Record<string, string>;
};

/** Ticks on every contract, and every leg places a target and a stop: what older builds read. */
export function legacyCompatible(p: BracketPreset): boolean {
  return p.unit === "ticks" && p.legs.every((l) => l.targetTicks !== null && l.stopTicks !== null);
}

/** The in-memory document as it is stored and synced. */
export function toStoredBracketDoc(doc: BracketDoc): StoredBracketDoc {
  const legacy = doc.presets.filter(legacyCompatible).map((p) => {
    const { unit: _unit, ...old } = stripEntered(p);
    return old;
  });
  return {
    v: BRACKET_DOC_VERSION,
    presets: legacy,
    templates_v2: doc.presets.filter((p) => !legacyCompatible(p)),
    order: doc.presets.map((p) => p.id),
    selected: doc.selected,
  };
}

/**
 * Parse a stored/server body (either form: `presets` alone from older builds, or with
 * `templates_v2`). Unknown or invalid templates are dropped; a `templates_v2` entry wins over a
 * `presets` entry with the same id (an older build may have written back a stale copy); `order`
 * sorts them, ids it does not name keep their place after it; selections pointing nowhere are kept
 * (they resolve to "No bracket"). `undefined` when the body is not a bracket document at all.
 */
export function sanitizeBracketDoc(raw: unknown): BracketDoc | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const r = raw as Record<string, unknown>;
  const legacy = r["presets"];
  const v2 = r["templates_v2"];
  if (!Array.isArray(legacy) && !Array.isArray(v2)) return undefined;
  const byId = new Map<string, BracketPreset>();
  const add = (list: unknown, wins: boolean): void => {
    if (!Array.isArray(list)) return;
    const seen = new Set<string>();
    for (const x of list) {
      const p = sanitizePreset(x);
      if (!p || seen.has(p.id)) continue;
      seen.add(p.id);
      if (wins || !byId.has(p.id)) byId.set(p.id, p);
    }
  };
  add(legacy, false);
  add(v2, true);
  const rawOrder = r["order"];
  const order = Array.isArray(rawOrder) ? rawOrder.filter((x): x is string => typeof x === "string") : [];
  const rank = (id: string): number => {
    const i = order.indexOf(id);
    return i < 0 ? order.length : i;
  };
  const presets = [...byId.values()]
    .map((p, i) => ({ p, i }))
    .sort((a, b) => rank(a.p.id) - rank(b.p.id) || a.i - b.i)
    .map((x) => x.p)
    .slice(0, MAX_PRESETS);
  const selected: Record<string, string> = {};
  const sel = r["selected"];
  if (typeof sel === "object" && sel !== null) {
    for (const [k, v] of Object.entries(sel as Record<string, unknown>)) {
      if (typeof v === "string") selected[symbolRoot(k)] = v;
    }
  }
  return { v: BRACKET_DOC_VERSION, presets, selected };
}
